/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from "react";
import { IconX } from "@tabler/icons-react";

import Button, { ButtonProps, ButtonType } from "../Button/Button";
import zIndex from "../utils/zIndex";

interface ModalProps {
  title?: string;
  show: boolean;
  closable?: boolean;
  onClose?: () => void;
  okText?: string;
  /** A subset of button props */
  okProps?: Pick<ButtonProps, "icon" | "iconPosition">
  onOk?: () => void;
  cancelText?: string;
  /** A subset of button props */
  cancelProps?: Pick<ButtonProps, "icon" | "iconPosition">
  onCancel?: () => void;

  /** Should we slightly hide the other components */
  overlay?: boolean;
  /** Clicking on overlay will close it */
  allowClickAway?: boolean;

  /**
   * Don't keep the object in DOM when not showing. If false, the object remains,
   * but is hidden.
   */
  unmountOnClose?: boolean;
}

const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  show = false,
  closable = true,
  onClose,
  title,
  okText,
  okProps,
  onOk,
  cancelText,
  cancelProps,
  onCancel,
  overlay = true,
  allowClickAway = true,
  unmountOnClose = false,
  children,
}) => {
  if (!show && unmountOnClose) {
    return null;
  }

  return (
    <div
      className={`shadow-xl rounded-md absolute flex justify-center items-center w-full h-full top-0 left-0 ${overlay ? "bg-overlay-50" : ""}`}
      style={{
        zIndex: zIndex.modal,
        transition: !show ? "visibility 0.2s, opacity 0.2s" : "visibility 0.1s, opacity 0.1s",
        visibility: show ? "visible" : "hidden",
        opacity: show ? 1 : 0,
      }}
      onClick={allowClickAway && closable ? () => onClose() : undefined}
    >
      <div
        className=""
        style={{
          minWidth: "min(600px, 90%)",
          maxWidth: "90%",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex flex-row items-center rounded-t-md px-4 py-4 md:py-6 typography-large-strong bg-bg-subtle border-b border-b-border-base select-none">
            <span className="flex-1">{title}</span>
            {closable && (
              <button type="button" onClick={() => onClose()}>
                <IconX className="w-5 h-5 text-icon-bg-subtle hover:text-icon-bg-base" />
              </button>
            )}
          </div>
        )}
        <div className={`bg-bg-components-base p-4 typography-medium-base ${!title ? "rounded-t-md" : ""} ${!(okText || cancelText) ? "rounded-b-md" : ""}`}>
          {children}
        </div>
        {(okText || cancelText) && (
          <div className="bg-bg-components-base p-4 rounded-b-md border-t border-t-border-base">
            <div className="flex flex-col md:items-stretch md:flex-row-reverse gap-2">
              {okText && (
                <Button
                  onClick={onOk}
                  icon={okProps?.icon}
                  iconPosition={okProps?.iconPosition}
                  type={ButtonType.primary}
                >
                  {okText}
                </Button>
              )}
              {cancelText && (
                <Button
                  onClick={onCancel}
                  icon={cancelProps?.icon}
                  iconPosition={cancelProps?.iconPosition}
                  type={ButtonType.neutral}
                >
                  {cancelText}
                </Button>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Modal;
